import { useEffect, useRef, useState } from "react";
import {
  BrowserMultiFormatReader,
  NotFoundException,
  ChecksumException,
  FormatException,
  DecodeHintType,
  BarcodeFormat,
} from "@zxing/library";
import "./CamScanner.css";

export default function CamScanner({ onDetected, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);

  const [error, setError] = useState("");
  const [debugMsg, setDebugMsg] = useState("");
  const [camRes, setCamRes] = useState("");

  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState("");

  const [scanPhase, setScanPhase] = useState(0);
  const lastScanUpdateRef = useRef(0);

  useEffect(() => {
    if (typeof window === "undefined") {
      setError("브라우저 환경이 아니라서 카메라를 사용할 수 없습니다.");
      return;
    }

    if (!window.isSecureContext) {
      setError("HTTPS(보안 연결)가 아니라서 모바일 브라우저에서 카메라가 차단되었습니다.");
      return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("이 브라우저에서는 카메라 API를 지원하지 않거나 설정에서 차단되어 있습니다.");
      return;
    }

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    hints.set(DecodeHintType.CHARACTER_SET, "utf-8");

    codeReaderRef.current = new BrowserMultiFormatReader(hints);

    const init = async () => {
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const videos = all.filter((d) => d.kind === "videoinput");

        const nonFront = videos.filter((dev) => {
          const label = (dev.label || "").toLowerCase();
          return !(
            label.includes("front") ||
            label.includes("user") ||
            label.includes("face") ||
            label.includes("selfie") ||
            label.includes("전면")
          );
        });

        const finalVideos = (nonFront.length ? nonFront : videos).slice();

        finalVideos.sort((a, b) => {
          const aa = a.deviceId || "";
          const bb = b.deviceId || "";
          return aa.localeCompare(bb);
        });

        setVideoDevices(finalVideos);

        let defaultId = null;
        if (finalVideos.length > 0) {
          defaultId = finalVideos[0].deviceId || null;
          setSelectedDeviceId(defaultId || "");
        }

        startDecode(defaultId);
      } catch (e) {
        console.error("init/enumerateDevices error:", e);
        setError("카메라 장치 정보를 가져올 수 없습니다.");
      }
    };

    init();

    return () => {
      stopDecodeAndStream();
    };
  }, []);

  const syncCanvasSize = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;

    if (!vw || !vh) return;

    canvas.width = vw;
    canvas.height = vh;

    drawBox(null);
    setCamRes(`${vw} x ${vh}`);
  };

  const getVideoLayout = () => {
    const video = videoRef.current;
    if (!video) return null;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const cw = video.clientWidth;
    const ch = video.clientHeight;

    if (!vw || !vh || !cw || !ch) return null;

    const videoRatio = vw / vh;
    const containerRatio = cw / ch;

    let drawWidth = 0;
    let drawHeight = 0;
    let offsetX = 0;
    let offsetY = 0;

    if (videoRatio > containerRatio) {
      drawHeight = ch;
      drawWidth = ch * videoRatio;
      offsetX = (cw - drawWidth) / 2;
      offsetY = 0;
    } else {
      drawWidth = cw;
      drawHeight = cw / videoRatio;
      offsetX = 0;
      offsetY = (ch - drawHeight) / 2;
    }

    return {
      vw,
      vh,
      cw,
      ch,
      drawWidth,
      drawHeight,
      offsetX,
      offsetY,
    };
  };

  const getGuideRect = () => {
    const layout = getVideoLayout();
    if (!layout) return null;

    const { vw, vh, cw, ch, drawWidth, drawHeight, offsetX, offsetY } = layout;

    const boxSize = Math.min(cw, ch) * 0.6;

    const guideW = boxSize;
    const guideH = boxSize;
    const guideX = (cw - guideW) / 2;
    const guideY = (ch - guideH) / 2;

    const roiX = ((guideX - offsetX) / drawWidth) * vw;
    const roiY = ((guideY - offsetY) / drawHeight) * vh;
    const roiW = (guideW / drawWidth) * vw;
    const roiH = (guideH / drawHeight) * vh;

    return { roiX, roiY, roiW, roiH };
  };

  const isInGuideArea = (result) => {
    const rect = getGuideRect();
    if (!rect || !result?.getResultPoints) {
      return true;
    }

    const points = result.getResultPoints();
    if (!points || points.length === 0) {
      return true;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (let i = 0; i < points.length; i += 1) {
      const x = points[i].getX();
      const y = points[i].getY();

      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }

    const { roiX, roiY, roiW, roiH } = rect;

    return (
      minX >= roiX &&
      minY >= roiY &&
      maxX <= roiX + roiW &&
      maxY <= roiY + roiH
    );
  };

  const stopDecodeAndStream = () => {
    if (codeReaderRef.current?.reset) {
      codeReaderRef.current.reset();
    }

    const stream = streamRef.current;
    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    const v = videoRef.current;
    if (v && v.srcObject instanceof MediaStream) {
      v.srcObject.getTracks().forEach((t) => t.stop());
      v.srcObject = null;
    }

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
  };

  const handleClose = () => {
    stopDecodeAndStream();
    onClose?.();
  };

  const handleDetected = (text) => {
    stopDecodeAndStream();
    setDebugMsg(`검출됨: ${text}`);
    onDetected?.(text);
  };

  const handleCameraChange = (e) => {
    const deviceId = e.target.value;
    setSelectedDeviceId(deviceId);
    stopDecodeAndStream();
    startDecode(deviceId);
  };

  const drawBox = (result) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!result?.getResultPoints) {
      return;
    }

    const points = result.getResultPoints();
    if (!points || points.length === 0) return;

    const inside = isInGuideArea(result);

    ctx.lineWidth = 2;
    ctx.strokeStyle = inside ? "lime" : "red";

    ctx.beginPath();
    ctx.moveTo(points[0].getX(), points[0].getY());
    for (let i = 1; i < points.length; i += 1) {
      ctx.lineTo(points[i].getX(), points[i].getY());
    }
    ctx.closePath();
    ctx.stroke();

    // if (!inside) {
    //   ctx.fillStyle = "red";
    //   ctx.font = "bold 20px Arial";
    //   ctx.textAlign = "center";
    //   // 비디오 해상도 기준 중앙에 텍스트 그리기
    //   ctx.fillText("QR 박스 내부로", canvas.width / 2, canvas.height / 2);
    // }
  };

  const startDecode = (deviceId = null) => {
    if (!codeReaderRef.current) {
      return;
    }

    setDebugMsg("QR 디코딩 시작…");
    setScanPhase(0);

    const constraints = {
      video: {
        width: { min: 640, ideal: 1280 },
        height: { min: 480, ideal: 720 },
        ...(deviceId
          ? { deviceId: { exact: deviceId } }
          : { facingMode: { ideal: "environment" } }),
      },
      audio: false,
    };

    codeReaderRef.current
      .decodeFromConstraints(constraints, "qr-video", (result, err) => {
        if (result) {
          drawBox(result);

          if (!isInGuideArea(result)) {
            setDebugMsg("QR을 중앙 가이드 박스 안에 맞춰 주세요.");
            return;
          }

          const text = result.getText();
          setDebugMsg(`QR 검출 성공: ${text}`);
          handleDetected(text);
          return;
        }

        if (err) {
          if (err instanceof NotFoundException) {
            const now = (typeof performance !== "undefined" && performance.now)
              ? performance.now()
              : Date.now();

            if (now - lastScanUpdateRef.current >= 300) {
              lastScanUpdateRef.current = now;
              drawBox(null);

              setScanPhase((prev) => {
                const next = (prev + 1) % 4;
                setDebugMsg(`QR 스캔 중${".".repeat(next)}`);
                return next;
              });
            }

            return;
          }

          if (err instanceof ChecksumException || err instanceof FormatException) {
            return;
          }

          setDebugMsg(`디코드 에러: ${err.message || String(err)}`);
        }
      })
      .catch((e) => {
        console.error("decodeFromConstraints error:", e);
        setDebugMsg(`디코딩 시작 실패: ${e.message || String(e)}`);
      });
  };

  return (
    <div className="cam-fullscreen">
      <video
        id="qr-video"
        ref={videoRef}
        className="cam-video"
        autoPlay
        playsInline
        muted
        onLoadedMetadata={syncCanvasSize}
      />
      <div className="cam-guide-box" />
      <canvas ref={canvasRef} className="cam-canvas" />

      <div className="cam-top-ui">
        {camRes && (
          <div className="cam-debug">
            {camRes}
          </div>
        )}
        <button
          type="button"
          onClick={handleClose}
          className="cam-close-btn"
        >
          X
        </button>
      </div>

      {error && <div className="cam-msg cam-error">{error}</div>}
      {debugMsg && <div className="cam-msg cam-debug">{debugMsg}</div>}

      {videoDevices.length > 0 && (
        <div className="cam-msg cam-select-wrap">
          <select
            className="cam-select"
            value={selectedDeviceId}
            onChange={handleCameraChange}
          >
            {videoDevices.map((dev, idx) => (
              <option key={dev.deviceId || idx} value={dev.deviceId}>
                {dev.label || `카메라 ${idx + 1}`}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}